import { find, matches, isNull, snakeCase } from 'lodash-es';

import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { schema as unfrozenSchema } from '../../../schema';
import {
    convertUnits,
    convertUnitsForDisplay,
    replaceDistanceUnit,
    replaceHelpTextMinMaxValues,
} from '../../../utils/formatting';
import {
    getTimezoneGraphqlEnumOptions,
    graphqlTimezoneToIana,
    safeIanaTimezoneToGraphQLEnum,
} from '../../../utils/timezone';
import { useProfile } from '../../../profile';
import { SwarmType } from '../../../enums';
import deepFreeze from '../../../utils/deepFreeze';
import { FieldsArray, findByName } from './fields-array';

const schema = deepFreeze(unfrozenSchema);

function findRawFormSchema(swarmType, isProject) {
    const formSchema = find(schema.forms, (s) =>
        matches({ swarmType, isProject })(s.properties)
    )?.schema;
    if (!formSchema) {
        throw new Error(`No form schema found for swarm:${swarmType} isProject:${isProject}.`);
    }
    return formSchema;
}

export function getReturnFields(swarmType, isProject) {
    return findRawFormSchema(swarmType, isProject).returnFields;
}

export function getFormSchema(swarmType, isProject, profile) {
    const formSchema = findRawFormSchema(swarmType, isProject);

    function addInGroupTitlePlaceholder([name, childGroup]) {
        return [
            name,
            null,
            Array.isArray(childGroup) ? childGroup.map(addInGroupTitlePlaceholder) : childGroup,
        ];
    }

    const layout = formSchema.layout.map(addInGroupTitlePlaceholder);

    const fields = FieldsArray.from(
        formSchema.fields.map((originalField) => {
            // Copy the field in order to safely manipulate the schema.
            const field = { ...originalField };

            // Keep validator bounds exact, but round the default for user-facing display. The
            // bounds are converted first, because the display rounding keeps the default within
            // the (converted) bounds.
            field.minValue = convertUnits(field, field.minValue, profile);
            field.maxValue = convertUnits(field, field.maxValue, profile);
            field.default = convertUnitsForDisplay(field, field.default, profile);

            if (field.name === 'timezone') {
                // The backend strips timezone options from per-form field payloads to keep
                // schema payloads small, so we backfill from the shared timezone map.
                field.options = getTimezoneGraphqlEnumOptions();
            }

            return Object.freeze(field);
        })
    );

    return Object.freeze({
        ...formSchema,
        fields: Object.freeze(FieldsArray.from(fields)),
        layout: Object.freeze(layout),
    });
}

export function createTranslatedFormSchema(swarmType, isProject, i18n, profile) {
    const formSchema = getFormSchema(swarmType, isProject, profile);
    const { t } = i18n;

    const fields = formSchema.fields.map((field) => {
        const fieldPrefix = `${formSchema.translationPrefix}__FIELD__${field.name}`;

        const translatedItems = {};

        ['label', 'helpText', 'placeholder'].forEach((fieldName) => {
            const key = `${fieldPrefix}__${snakeCase(fieldName).toUpperCase()}`;

            if (i18n.exists(key)) {
                const needsConversion = field.unitConversion === 'mm_to_user';
                if (needsConversion) {
                    const isHelpText = fieldName === 'helpText';
                    const text = isHelpText
                        ? replaceHelpTextMinMaxValues(t(key), field, profile.distanceUnit)
                        : t(key);
                    translatedItems[fieldName] = replaceDistanceUnit(text, profile.distanceUnit);
                } else {
                    translatedItems[fieldName] = t(key);
                }
            }
        });

        if ('options' in field) {
            if (field.name === 'timezone') {
                // Timezone options use GraphQL enum keys as value; render their IANA label.
                translatedItems.options = field.options.map((optionKey) => [
                    optionKey,
                    graphqlTimezoneToIana(optionKey),
                ]);
            } else {
                translatedItems.options = field.options.map((optionKey) => [
                    optionKey,
                    t(`${fieldPrefix}__OPTION__${isNull(optionKey) ? 'None' : optionKey}`),
                ]);
            }
        }

        if (field.name === 'notificationConfig') {
            const alarmTranslations = t(`${fieldPrefix}__ALARMS`, { returnObjects: true });

            translatedItems.alarms = field.alarms.map((alarm) => {
                const translations = findByName(alarmTranslations, alarm.name);

                return {
                    ...alarm,
                    ...translations,
                    fields: alarm.fields.map((alarmField) => {
                        const translatedAlarmField = findByName(
                            translations.fields,
                            alarmField.name
                        );

                        return {
                            ...alarmField,
                            ...translatedAlarmField,
                            options: translatedAlarmField.choices,
                        };
                    }),
                };
            });
        }

        return Object.freeze({
            ...field,
            ...translatedItems,
        });
    });

    function replaceFieldDefault(fieldName, rawValue) {
        const field = fields.getByName(fieldName);

        fields.replaceByName(
            fieldName,
            Object.freeze({
                ...field,
                // The field's bounds are already converted, so the default is rounded for
                // display within those bounds, like the schema defaults in `getFormSchema`.
                default: convertUnitsForDisplay(field, rawValue, profile),
            })
        );
    }

    replaceFieldDefault('timezone', safeIanaTimezoneToGraphQLEnum(profile.preferredTimezone));

    if (swarmType === SwarmType.Vibration) {
        Object.entries(profile.defaultVibrationSettings).forEach(([fieldName, rawValue]) => {
            replaceFieldDefault(fieldName, rawValue);
        });
    }

    const information = Object.fromEntries(
        formSchema.information.map((key) => [
            key,
            t(`${formSchema.translationPrefix}__INFO__${key}`),
        ])
    );

    function addInGroupTitle([name, _titlePlaceholder, childGroup]) {
        return [
            name,
            t(`${formSchema.translationPrefix}__LAYOUT__${name}`),
            Array.isArray(childGroup) ? childGroup.map(addInGroupTitle) : childGroup,
        ];
    }

    const layout = formSchema.layout.map(addInGroupTitle);

    return Object.freeze({
        ...formSchema,
        fields: Object.freeze(fields),
        layout: Object.freeze(layout),
        information: Object.freeze(information),
    });
}

export function useFormSchema(swarmType, isProject) {
    const profile = useProfile();
    return useMemo(
        () => getFormSchema(swarmType, isProject, profile),
        [swarmType, isProject, profile]
    );
}

export function useTranslatedFormSchema(swarmType, isProject) {
    const { i18n } = useTranslation();
    const profile = useProfile();

    return useMemo(
        () => createTranslatedFormSchema(swarmType, isProject, i18n, profile),
        [swarmType, isProject, i18n, profile]
    );
}
